import React, { useEffect, useMemo, useRef, useState } from 'react'

import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import { DatesSetArg, EventInput } from '@fullcalendar/core'

import {
    Box,
    Button,
    Chip,
    CircularProgress,
    makeStyles,
    Theme,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import {
    Add as AddIcon,
    ViewList as ListIcon,
    PersonAdd as MoveInIcon,
    EmojiPeople as MoveOutIcon,
    Event as EventIcon,
    DirectionsWalk as TourIcon,
} from '@material-ui/icons'

import { DateTime } from 'luxon'

import { usePropertyEvents } from '../../hooks/usePropertyEvents'
import { useTours } from '../../hooks/Outreach/useTours'
import { useLease, useService, useCompany, useAptConfig } from '../../hooks'
import { useTimezone } from '../../contexts/timezone'
import { LeaseCluster } from '../../models'
import { ApartmentTour } from '../../models/Outreach/types'
import { PropertyEvent } from '../../models/PropertyEvent'
import { CalendarEventType, LeaseModalFormState } from '../ScheduleHome/types'
import { LeaseModal } from '../ScheduleHome/ViewComponents/LeaseModal/LeaseModal'
import { EventTypeWrapper } from '../ScheduleHome/ViewComponents/Calendar/EventRenderers/EventTypeWrapper'
import { EventTypeMoveout } from '../ScheduleHome/ViewComponents/Calendar/EventRenderers/EventTypeMoveout'
import { EventTypeMoveIn } from '../ScheduleHome/ViewComponents/Calendar/EventRenderers/EventTypeMoveIn'
import { PropertyEventModal } from './PropertyEventModal'
import { EventListModal } from './EventListModal'
import { TourDayModal } from './TourDayModal'
import { ScheduleTourModal } from './ScheduleTourModal'
import { DateClickPopover } from './DateClickPopover'
import './PropertyEvents.css'
import '../ScheduleHome/ViewComponents/Calendar/Calendar.css'

type CalendarLayer = 'events' | 'move_in' | 'move_out' | 'tours'

type LeaseClusterEventType = 'MOVE_IN' | 'MOVE_OUT'

interface TourDayCluster {
    date: string
    count: number
    tours: ApartmentTour[]
}

const useStyles = makeStyles((theme: Theme) => ({
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(2),
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
    },
    titleArea: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
    },
    eventCount: {
        backgroundColor: `${theme.palette.primary.main}14`,
        color: theme.palette.primary.main,
        fontWeight: 700,
        fontSize: '0.75rem',
        height: 24,
    },
    layerToggles: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        flexWrap: 'wrap',
    },
    layerChip: {
        fontWeight: 600,
        fontSize: '0.75rem',
        height: 28,
        borderRadius: 8,
        cursor: 'pointer',
    },
    layerChipActive: {
        backgroundColor: `${theme.palette.primary.main}14`,
        color: theme.palette.primary.main,
        border: `1.5px solid ${theme.palette.primary.main}`,
    },
    layerChipInactive: {
        backgroundColor: '#fff',
        color: theme.palette.text.secondary,
        border: '1.5px solid #e6e9ed',
        '&:hover': {
            borderColor: '#b0b8c1',
        },
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    listButton: {
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 600,
        padding: theme.spacing(0.75, 1.5),
        border: `1.5px solid #e6e9ed`,
        color: theme.palette.text.secondary,
        '&:hover': {
            borderColor: theme.palette.primary.light,
            backgroundColor: `${theme.palette.primary.main}04`,
        },
    },
    createButton: {
        borderRadius: 10,
        textTransform: 'none',
        fontWeight: 600,
        padding: theme.spacing(1, 2.5),
        boxShadow: '0 2px 8px rgba(0, 140, 133, 0.25)',
        '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 140, 133, 0.35)',
        },
    },
}))

export const PropertyEventsHome: React.FC = () => {
    const theme = useTheme()
    const classes = useStyles()
    const calendarRef = useRef<any>(null)
    const { currentTimezone } = useTimezone()

    const { getLeaseClusterList, leaseClusterList } = useLease()
    const { serviceList } = useService({ getServiceList: true, cleanUp: true })
    const { vendorList } = useCompany({ getVendorList: true, cleanUp: true })
    const { inspectionTypeList } = useAptConfig({
        inspectionTypeList: true,
        cleanUp: true,
    })

    const [dateRange, setDateRange] = useState<{
        startsAfter: string
        startsBefore: string
    }>(() => {
        const now = DateTime.now()
        const start = now.startOf('month').minus({ months: 1 })
        const end = now.endOf('month').plus({ months: 1 })
        return {
            startsAfter: start.toISO()!,
            startsBefore: end.toISO()!,
        }
    })

    const [modalOpen, setModalOpen] = useState(false)
    const [listModalOpen, setListModalOpen] = useState(false)
    const [selectedEvent, setSelectedEvent] = useState<PropertyEvent | null>(
        null,
    )
    const [selectedDate, setSelectedDate] = useState<DateTime | null>(null)
    const [leaseModal, setLeaseModal] = useState<LeaseModalFormState | null>(
        null,
    )
    const [tourDayModal, setTourDayModal] = useState<{
        dateKey: string
        tours: ApartmentTour[]
    } | null>(null)
    const [tourModalOpen, setTourModalOpen] = useState(false)
    const [tourModalDate, setTourModalDate] = useState<DateTime | null>(null)
    const [datePopover, setDatePopover] = useState<{
        position: { top: number; left: number }
        date: DateTime
    } | null>(null)
    const [visibleLayers, setVisibleLayers] = useState<
        Record<CalendarLayer, boolean>
    >({
        events: true,
        move_in: true,
        move_out: true,
        tours: true,
    })
    // Keep FullCalendar mounted after first paint so month navigation
    // refetch does not remount it back to the current month.
    const [hasShownCalendar, setHasShownCalendar] = useState(false)

    const {
        events,
        isLoading,
        createEvent,
        updateEvent,
        deleteEvent,
        draftMessage,
        sendEventMessage,
    } = usePropertyEvents({
        startsAfter: dateRange.startsAfter,
        startsBefore: dateRange.startsBefore,
    })

    const { tours } = useTours({
        startsAfter: dateRange.startsAfter,
        startsBefore: dateRange.startsBefore,
    })

    const reloadLeaseClusters = () => {
        getLeaseClusterList({ params: { exclude_children: true } })
    }

    useEffect(() => {
        reloadLeaseClusters()
    }, [])

    useEffect(() => {
        if (!isLoading) {
            setHasShownCalendar(true)
        }
    }, [isLoading])

    useEffect(() => {
        if (!selectedEvent) return
        const refreshed = events.find((e) => e.id === selectedEvent.id)
        if (
            refreshed &&
            refreshed.modified_date !== selectedEvent.modified_date
        ) {
            setSelectedEvent(refreshed)
        }
    }, [events])

    const handleDatesSet = (arg: DatesSetArg) => {
        const start = DateTime.fromJSDate(arg.start).minus({ months: 1 })
        const end = DateTime.fromJSDate(arg.end).plus({ months: 1 })
        const startsAfter = start.toISO()!
        const startsBefore = end.toISO()!
        setDateRange((prev) => {
            if (
                prev.startsAfter === startsAfter &&
                prev.startsBefore === startsBefore
            ) {
                return prev
            }
            return { startsAfter, startsBefore }
        })
        reloadLeaseClusters()
    }

    const handleDateClick = (info: { date: Date; jsEvent: MouseEvent }) => {
        setDatePopover({
            position: { top: info.jsEvent.clientY, left: info.jsEvent.clientX },
            date: DateTime.fromJSDate(info.date).setZone(currentTimezone, {
                keepLocalTime: true,
            }),
        })
    }

    const handleEventClick = (info: { event: any }) => {
        const tourCluster = info.event.extendedProps.tourCluster as
            | TourDayCluster
            | undefined
        if (tourCluster) {
            setTourDayModal({
                dateKey: tourCluster.date,
                tours: tourCluster.tours,
            })
            return
        }

        const leaseType = info.event.extendedProps.leaseClusterType as
            | LeaseClusterEventType
            | undefined
        const leaseCluster = info.event.extendedProps.leaseCluster as
            | LeaseCluster
            | undefined

        if (leaseType && leaseCluster) {
            setLeaseModal({
                type:
                    leaseType === 'MOVE_IN'
                        ? CalendarEventType.MOVE_IN
                        : CalendarEventType.MOVE_OUT,
                leaseCluster,
            })
            return
        }

        const eventData = info.event.extendedProps
            .propertyEvent as PropertyEvent
        if (eventData) {
            setSelectedEvent(eventData)
            setSelectedDate(null)
            setModalOpen(true)
        }
    }

    const handleCloseModal = () => {
        setModalOpen(false)
        setSelectedEvent(null)
        setSelectedDate(null)
    }

    const handleListEventClick = (event: PropertyEvent) => {
        setListModalOpen(false)
        setSelectedEvent(event)
        setSelectedDate(null)
        setModalOpen(true)
    }

    const toggleLayer = (layer: CalendarLayer) => {
        setVisibleLayers((prev) => ({ ...prev, [layer]: !prev[layer] }))
    }

    const propertyEvents: EventInput[] = useMemo(() => {
        if (!visibleLayers.events) return []

        return events.map((event) => {
            const startDt = DateTime.fromISO(event.starts_at).setZone(
                currentTimezone,
            )
            const endDt = DateTime.fromISO(event.ends_at).setZone(
                currentTimezone,
            )
            const isDraft = event.status === 'DRAFT'

            return {
                title: event.title,
                start: event.is_all_day
                    ? startDt.toFormat('yyyy-MM-dd')
                    : startDt.toISO()!,
                end: event.is_all_day
                    ? endDt.plus({ days: 1 }).toFormat('yyyy-MM-dd')
                    : endDt.toISO()!,
                allDay: event.is_all_day,
                backgroundColor: isDraft
                    ? '#f3f4f6'
                    : theme.palette.primary.main,
                borderColor: isDraft ? '#d1d5db' : theme.palette.primary.dark,
                textColor: isDraft ? '#374151' : '#fff',
                classNames: isDraft
                    ? ['property-event-draft']
                    : ['property-event'],
                extendedProps: {
                    propertyEvent: event,
                    startDt,
                },
            }
        })
    }, [events, currentTimezone, theme, visibleLayers.events])

    const leaseClusterEvents: EventInput[] = useMemo(() => {
        const result: EventInput[] = []

        if (visibleLayers.move_out) {
            const moveOuts = leaseClusterList?.move_out ?? []
            moveOuts.forEach((cluster) => {
                result.push({
                    title: `MOVE-OUT-${cluster.count}`,
                    start: cluster.date,
                    allDay: false,
                    extendedProps: {
                        leaseClusterType: 'MOVE_OUT' as LeaseClusterEventType,
                        leaseCluster: cluster,
                    },
                })
            })
        }

        if (visibleLayers.move_in) {
            const moveIns = leaseClusterList?.move_in ?? []
            moveIns.forEach((cluster) => {
                result.push({
                    title: `MOVE-IN-${cluster.count}`,
                    start: cluster.date,
                    allDay: false,
                    extendedProps: {
                        leaseClusterType: 'MOVE_IN' as LeaseClusterEventType,
                        leaseCluster: cluster,
                    },
                })
            })
        }

        return result
    }, [leaseClusterList, visibleLayers.move_in, visibleLayers.move_out])

    const tourClustersByDate = useMemo(() => {
        const groups: Record<string, ApartmentTour[]> = {}
        tours.forEach((tour) => {
            if (!tour.starts_at) return
            const dateKey = DateTime.fromISO(tour.starts_at, { zone: 'utc' })
                .setZone(currentTimezone)
                .toFormat('yyyy-MM-dd')
            if (!groups[dateKey]) groups[dateKey] = []
            groups[dateKey].push(tour)
        })
        return groups
    }, [tours, currentTimezone])

    useEffect(() => {
        setTourDayModal((prev) => {
            if (!prev) return prev
            const fresh = tourClustersByDate[prev.dateKey]
            if (!fresh) return prev
            return { ...prev, tours: fresh }
        })
    }, [tourClustersByDate])

    const tourClusterEvents: EventInput[] = useMemo(() => {
        if (!visibleLayers.tours) return []
        return Object.entries(tourClustersByDate).map(([date, dayTours]) => {
            const cluster: TourDayCluster = {
                date,
                count: dayTours.length,
                tours: dayTours,
            }
            return {
                title: `TOURS-${cluster.count}`,
                start: date,
                allDay: true,
                extendedProps: {
                    tourCluster: cluster,
                },
            }
        })
    }, [tourClustersByDate, visibleLayers.tours])

    const calendarEvents = useMemo(
        () => [...propertyEvents, ...leaseClusterEvents, ...tourClusterEvents],
        [propertyEvents, leaseClusterEvents, tourClusterEvents],
    )

    const moveInCount = (leaseClusterList?.move_in ?? []).reduce(
        (sum, c) => sum + c.count,
        0,
    )
    const moveOutCount = (leaseClusterList?.move_out ?? []).reduce(
        (sum, c) => sum + c.count,
        0,
    )
    const toursCount = tours.filter((t) => t.starts_at).length

    return (
        <Box display="flex" flexDirection="column" height="calc(100vh - 140px)">
            <Box className={classes.header}>
                <Box className={classes.titleArea}>
                    <Typography variant="h5" style={{ fontWeight: 700 }}>
                        Community Calendar
                    </Typography>
                    {events.length > 0 && (
                        <Chip
                            label={`${events.length} event${
                                events.length !== 1 ? 's' : ''
                            }`}
                            size="small"
                            className={classes.eventCount}
                        />
                    )}
                    <Box className={classes.layerToggles}>
                        <Chip
                            icon={<EventIcon style={{ fontSize: 16 }} />}
                            label="Events"
                            size="small"
                            variant="outlined"
                            className={`${classes.layerChip} ${
                                visibleLayers.events
                                    ? classes.layerChipActive
                                    : classes.layerChipInactive
                            }`}
                            onClick={() => toggleLayer('events')}
                        />
                        <Chip
                            icon={<MoveInIcon style={{ fontSize: 16 }} />}
                            label={`Move Ins (${moveInCount})`}
                            size="small"
                            variant="outlined"
                            className={`${classes.layerChip} ${
                                visibleLayers.move_in
                                    ? classes.layerChipActive
                                    : classes.layerChipInactive
                            }`}
                            onClick={() => toggleLayer('move_in')}
                        />
                        <Chip
                            icon={<MoveOutIcon style={{ fontSize: 16 }} />}
                            label={`Move Outs (${moveOutCount})`}
                            size="small"
                            variant="outlined"
                            className={`${classes.layerChip} ${
                                visibleLayers.move_out
                                    ? classes.layerChipActive
                                    : classes.layerChipInactive
                            }`}
                            onClick={() => toggleLayer('move_out')}
                        />
                        <Chip
                            icon={<TourIcon style={{ fontSize: 16 }} />}
                            label={`Tours (${toursCount})`}
                            size="small"
                            variant="outlined"
                            className={`${classes.layerChip} ${
                                visibleLayers.tours
                                    ? classes.layerChipActive
                                    : classes.layerChipInactive
                            }`}
                            onClick={() => toggleLayer('tours')}
                            style={
                                visibleLayers.tours
                                    ? {
                                          backgroundColor: '#ec489914',
                                          color: '#db2777',
                                          borderColor: '#ec4899',
                                      }
                                    : undefined
                            }
                        />
                    </Box>
                </Box>

                <Box className={classes.actions}>
                    <Tooltip title="View all events as a list">
                        <Button
                            className={classes.listButton}
                            startIcon={<ListIcon />}
                            onClick={() => setListModalOpen(true)}
                            size="small"
                        >
                            List View
                        </Button>
                    </Tooltip>

                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.createButton}
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setSelectedEvent(null)
                            setSelectedDate(
                                DateTime.now().setZone(currentTimezone),
                            )
                            setModalOpen(true)
                        }}
                    >
                        New Event
                    </Button>
                    <Button
                        variant="outlined"
                        className={classes.listButton}
                        startIcon={<TourIcon style={{ color: '#db2777' }} />}
                        onClick={() => {
                            setTourModalDate(
                                DateTime.now().setZone(currentTimezone),
                            )
                            setTourModalOpen(true)
                        }}
                        style={{ borderColor: '#ec4899', color: '#db2777' }}
                    >
                        Schedule Tour
                    </Button>
                </Box>
            </Box>

            {isLoading && !hasShownCalendar ? (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flex={1}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <Box flex={1} minHeight={0}>
                    <FullCalendar
                        ref={calendarRef}
                        plugins={[dayGridPlugin, interactionPlugin]}
                        initialView="dayGridMonth"
                        events={calendarEvents}
                        height="100%"
                        datesSet={handleDatesSet}
                        dateClick={handleDateClick}
                        eventClick={handleEventClick}
                        headerToolbar={{
                            left: 'today prev,next',
                            center: 'title',
                            right: '',
                        }}
                        eventContent={(arg) => {
                            const tourCluster = arg.event.extendedProps
                                .tourCluster as TourDayCluster | undefined
                            if (tourCluster) {
                                return (
                                    <div
                                        className="pe-cell"
                                        style={{
                                            backgroundColor: '#fce7f3',
                                            borderColor: '#ec4899',
                                            color: '#9d174d',
                                            cursor: 'pointer',
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setTourDayModal({
                                                dateKey: tourCluster.date,
                                                tours: tourCluster.tours,
                                            })
                                        }}
                                    >
                                        <TourIcon
                                            className="pe-event-icon"
                                            style={{
                                                fontSize: 16,
                                                color: '#db2777',
                                            }}
                                        />
                                        <span className="pe-title">
                                            {tourCluster.count} Tour
                                            {tourCluster.count !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                )
                            }

                            const leaseType = arg.event.extendedProps
                                .leaseClusterType as
                                | LeaseClusterEventType
                                | undefined
                            const leaseCluster = arg.event.extendedProps
                                .leaseCluster as LeaseCluster | undefined

                            if (leaseType && leaseCluster) {
                                if (leaseType === 'MOVE_OUT') {
                                    let htmlColor = theme.palette.grey[800]
                                    if (
                                        leaseCluster.move_out_rule_count &&
                                        leaseCluster.move_out_rule_count !== 0
                                    ) {
                                        if (
                                            leaseCluster.move_out_rule_count ===
                                            leaseCluster.count
                                        ) {
                                            htmlColor =
                                                theme.palette.primary.dark
                                        } else {
                                            htmlColor =
                                                theme.palette.warning.main
                                        }
                                    }

                                    return (
                                        <EventTypeWrapper
                                            theme={theme}
                                            eventType={
                                                CalendarEventType.MOVE_OUT
                                            }
                                            htmlColor={htmlColor}
                                        >
                                            <EventTypeMoveout
                                                theme={theme}
                                                leaseCluster={leaseCluster}
                                                onClick={(_e, cluster) => {
                                                    setLeaseModal({
                                                        type:
                                                            CalendarEventType.MOVE_OUT,
                                                        leaseCluster: cluster,
                                                    })
                                                }}
                                            />
                                        </EventTypeWrapper>
                                    )
                                }

                                return (
                                    <EventTypeWrapper
                                        theme={theme}
                                        eventType={CalendarEventType.MOVE_IN}
                                    >
                                        <EventTypeMoveIn
                                            theme={theme}
                                            leaseCluster={leaseCluster}
                                            onClick={(_e, cluster) => {
                                                setLeaseModal({
                                                    type:
                                                        CalendarEventType.MOVE_IN,
                                                    leaseCluster: cluster,
                                                })
                                            }}
                                        />
                                    </EventTypeWrapper>
                                )
                            }

                            const pe = arg.event.extendedProps.propertyEvent as
                                | PropertyEvent
                                | undefined
                            if (!pe) return null

                            const isDraft = pe.status === 'DRAFT'
                            const startDt = arg.event.extendedProps.startDt as
                                | DateTime
                                | undefined
                            const timeStr =
                                !pe.is_all_day && startDt
                                    ? startDt.toFormat('h:mm a')
                                    : null

                            return (
                                <div
                                    className={`pe-cell ${
                                        isDraft ? 'pe-cell--draft' : ''
                                    }`}
                                >
                                    <EventIcon
                                        className="pe-event-icon"
                                        style={{ fontSize: 16 }}
                                    />
                                    {timeStr && (
                                        <span className="pe-time">
                                            {timeStr}
                                        </span>
                                    )}
                                    <span
                                        className={`pe-title ${
                                            isDraft ? 'pe-title--draft' : ''
                                        }`}
                                    >
                                        {arg.event.title}
                                    </span>
                                    {isDraft && (
                                        <span className="pe-badge pe-badge--draft">
                                            Draft
                                        </span>
                                    )}
                                </div>
                            )
                        }}
                    />
                </Box>
            )}

            <PropertyEventModal
                open={modalOpen}
                onClose={handleCloseModal}
                event={selectedEvent}
                initialDate={selectedDate}
                timezone={currentTimezone}
                createEvent={createEvent}
                updateEvent={updateEvent}
                deleteEvent={deleteEvent}
                draftMessage={draftMessage}
                sendEventMessage={sendEventMessage}
                onEventUpdated={setSelectedEvent}
            />

            <EventListModal
                open={listModalOpen}
                onClose={() => setListModalOpen(false)}
                events={events}
                onEventClick={handleListEventClick}
                timezone={currentTimezone}
            />

            <LeaseModal
                open={leaseModal}
                handleClose={() => setLeaseModal(null)}
                theme={theme}
                vendorList={vendorList}
                serviceList={serviceList}
                inspectionTypeList={inspectionTypeList ?? []}
                onReloadData={reloadLeaseClusters}
            />

            <TourDayModal
                open={tourDayModal !== null}
                onClose={() => setTourDayModal(null)}
                dateKey={tourDayModal?.dateKey ?? null}
                tours={tourDayModal?.tours ?? []}
                timezone={currentTimezone}
            />

            <ScheduleTourModal
                open={tourModalOpen}
                onClose={() => setTourModalOpen(false)}
                initialDate={tourModalDate}
            />

            <DateClickPopover
                anchorPosition={datePopover?.position ?? null}
                onClose={() => setDatePopover(null)}
                onSelectEvent={() => {
                    if (datePopover) {
                        setSelectedEvent(null)
                        setSelectedDate(datePopover.date)
                        setModalOpen(true)
                    }
                }}
                onSelectTour={() => {
                    if (datePopover) {
                        setTourModalDate(datePopover.date)
                        setTourModalOpen(true)
                    }
                }}
            />
        </Box>
    )
}
